import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

type Theme = 'light' | 'dark';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
  pageTransition: (callback: () => void) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  // Initialize theme state from localStorage or system preference
  const [theme, setTheme] = useState<Theme>(() => {
    // Check for saved theme preference
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme === 'light' || savedTheme === 'dark') {
      return savedTheme;
    }
    
    // Otherwise check system preference
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
    
    // Default to dark mode to match The League System App design
    return 'dark';
  });

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e: MediaQueryListEvent) => {
      // Only change theme based on system preference if no manual choice is saved
      if (!localStorage.getItem('theme')) {
        setTheme(e.matches ? 'dark' : 'light');
      }
    };
    
    // Add event listener with compatibility for older browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
    } else {
      // For older browsers
      mediaQuery.addListener(handleChange);
    }
    
    // Clean up
    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleChange);
      } else {
        // For older browsers
        mediaQuery.removeListener(handleChange);
      }
    };
  }, []);
  
  // Apply theme classes consistently across the application
  useEffect(() => {
    // Apply to root html element for Tailwind dark mode
    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
      root.classList.remove('light');
    } else {
      root.classList.add('light');
      root.classList.remove('dark');
    }
    
    // Apply to body as well for consistent application
    document.body.classList.remove('light', 'dark', 'light-theme', 'theme-transition');
    document.body.classList.add(theme);
    
    // For legacy support with the light-theme class in Layout.css
    if (theme === 'light') {
      document.body.classList.add('light-theme');
    }
    
    // Enable transitions after initial load with a short delay to prevent transition on first render
    setTimeout(() => {
      document.body.classList.add('theme-transition');
    }, 50);
    
    // Apply to body attribute for potential CSS targeting
    document.body.setAttribute('data-theme', theme);
    
    // Update meta theme-color for mobile browsers
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute(
        'content',
        theme === 'dark' ? '#111827' : '#f5f7fa'
      );
    }
    
    // Set CSS custom property for direct use in components
    document.documentElement.style.setProperty('--current-theme', theme);
    
    // Force repaint in some browsers where CSS variables don't update properly
    const forceRepaint = () => {
      // This hidden reflow trick forces a repaint
      document.body.style.display = 'none';
      // Force a reflow
      void document.body.offsetHeight;
      document.body.style.display = '';
    };
    
    // Delay the force repaint slightly to ensure other state updates have completed
    setTimeout(forceRepaint, 10);
    
    // Store preference
    localStorage.setItem('theme', theme);
  }, [theme]);
  
  // Toggle theme function with transition
  const toggleTheme = () => {
    // First, ensure the theme-transition class is applied
    document.body.classList.add('theme-transition');
    
    // Then toggle the theme after a tiny delay to ensure transitions are active
    setTimeout(() => {
      setTheme(prevTheme => {
        const newTheme = prevTheme === 'light' ? 'dark' : 'light';
        localStorage.setItem('theme', newTheme);
        return newTheme;
      });
    }, 10);
  };

  // Helper function for page transitions
  const pageTransition = (callback: () => void) => {
    // Create a clean page transition effect
    document.body.style.opacity = '0';
    document.body.style.transition = 'opacity 0.2s ease-out';
    
    setTimeout(() => {
      callback();
      setTimeout(() => {
        document.body.style.opacity = '1';
      }, 50);
    }, 200);
  };
  
  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, pageTransition }}>
      {children}
    </ThemeContext.Provider>
  );
};

// Custom hook for using the theme
export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeContext;